import { BackgroundAttributes } from '@redfox-thank-you/control-groups';
import { createAttributeManager, StyleRenderer } from '@redfox-thank-you/utils';
import { RichText, useBlockProps } from '@wordpress/block-editor';
import { Notice } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { Settings } from './settings';
import { generateOrderInfoCSS } from './style';
import type { OrderInfoEditProps, OrderInfoType } from './types';

/**
 * Order Info block edit component.
 */
const OrderInfoEdit: React.FC< OrderInfoEditProps > = ( {
	attributes,
	setAttributes,
} ) => {
	// Create attribute manager with utility functions
	const { getAttribute, updateAttributes } = createAttributeManager(
		attributes,
		setAttributes,
	);

	// Extract settings properties with defaults using getAttribute
	const infoType = getAttribute< OrderInfoType >(
		'settings.infoType',
		'order_number',
	);
	const customLabel = getAttribute< string >( 'settings.label.text', '' );
	const showLabel = getAttribute< boolean >( 'settings.label.show', true );

	// Check if we're in a redfoxwp_thank_you post type
	const postType = useSelect( ( select ) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getCurrentPostType?.() as string;
	}, [] );

	// Info type options
	const infoTypes = [
		{
			label: __( 'Order Number', 'redfox-thank-you' ),
			value: 'order_number',
		},
		{
			label: __( 'Order Date', 'redfox-thank-you' ),
			value: 'order_date',
		},
		{
			label: __( 'Total Amount', 'redfox-thank-you' ),
			value: 'total_amount',
		},
		{
			label: __( 'Payment Method', 'redfox-thank-you' ),
			value: 'payment_method',
		},
		{
			label: __( 'Order Status', 'redfox-thank-you' ),
			value: 'order_status',
		},
		{
			label: __( 'Subtotal', 'redfox-thank-you' ),
			value: 'subtotal',
		},
		{
			label: __( 'Shipping', 'redfox-thank-you' ),
			value: 'shipping',
		},
		{
			label: __( 'Tax', 'redfox-thank-you' ),
			value: 'tax',
		},
		{
			label: __( 'Discount', 'redfox-thank-you' ),
			value: 'discount',
		},
	];

	// Get the default label for the selected info type
	const getDefaultLabel = ( infoType: string ) => {
		const selectedType = infoTypes.find(
			( type ) => type.value === infoType,
		);
		return selectedType ? `${ selectedType.label }: ` : '';
	};

	// Use custom label or default label
	const displayLabel = customLabel || getDefaultLabel( infoType );

	// Helper function to update settings (needed for RichText onChange)
	const updateSettings = ( path: string, value: string ) => {
		if ( path === 'label.text' ) {
			updateAttributes( 'settings.label.text', value );
		}
	};

	// Generate unique block class based on attributes
	const generateBlockClass = () => {
		const cleanName = 'order-info';
		const hash = Math.random().toString( 36 ).substr( 2, 8 );
		return `redfox-thank-you-block-${ cleanName }-${ hash }`;
	};

	const [ blockClass ] = React.useState( () => generateBlockClass() );

	const blockProps = useBlockProps( {
		className: blockClass,
		style: {
			position: 'relative', // Ensure overlay positioning works correctly
		},
	} );

	// Show notice if not in redfoxwp_thank_you post type
	if ( postType !== 'redfoxwp_thank_you' ) {
		return (
			<div { ...blockProps }>
				<Notice status="warning" isDismissible={ false }>
					{ __(
						'The Order Info block can only be used in Thank You pages.',
						'redfox-thank-you',
					) }
				</Notice>
			</div>
		);
	}

	// Check if any overlay is enabled across responsive breakpoints
	const hasAnyOverlayEnabled = (
		backgroundStyle: BackgroundAttributes,
	): boolean => {
		// Check responsive structure
		const breakpoints = [ 'desktop', 'tablet', 'mobile' ] as const;
		return breakpoints.some( ( breakpoint ) => {
			const breakpointValues = backgroundStyle[ breakpoint ];
			return breakpointValues?.backgroundOverlay?.enabled;
		} );
	};

	// Extract background styles for overlay rendering using getAttribute
	const blockBackgroundStyle = getAttribute< BackgroundAttributes >(
		'style.block.background',
		{},
	);

	// Check if overlay is enabled
	const overlayEnabled = hasAnyOverlayEnabled( blockBackgroundStyle );

	// Get mock data for preview
	const getMockData = ( type: OrderInfoType ): string => {
		switch ( type ) {
			case 'order_number':
				return __( '#1234', 'redfox-thank-you' );
			case 'order_date':
				return __( '2025-05-28', 'redfox-thank-you' );
			case 'total_amount':
				return __( '$99.99', 'redfox-thank-you' );
			case 'payment_method':
				return __( 'Credit Card', 'redfox-thank-you' );
			case 'order_status':
				return __( 'Processing', 'redfox-thank-you' );
			case 'subtotal':
				return __( '$89.99', 'redfox-thank-you' );
			case 'shipping':
				return __( '$5.00', 'redfox-thank-you' );
			case 'tax':
				return __( '$5.00', 'redfox-thank-you' );
			case 'discount':
				return __( '$0.00', 'redfox-thank-you' );
			default:
				return __( 'Order Info', 'redfox-thank-you' );
		}
	};

	return (
		<>
			<StyleRenderer
				css={ generateOrderInfoCSS( attributes, blockClass ) }
				blockId={ blockClass }
			/>
			<Settings
				updateAttributes={ updateAttributes }
				getAttribute={ getAttribute }
			/>

			<div { ...blockProps }>
				{ /* Background Overlay - responsive aware */ }
				{ overlayEnabled && (
					<div className="redfox-thank-you-order-info__background-overlay" />
				) }

				<div className="redfox-thank-you-order-info__container">
					{ showLabel && (
						<RichText
							tagName="strong"
							className="redfox-thank-you-order-info__label"
							value={ displayLabel }
							onChange={ ( value: string ) =>
								updateSettings( 'label.text', value )
							}
							placeholder={ getDefaultLabel( infoType ) }
							allowedFormats={ [ 'core/bold', 'core/italic' ] }
						/>
					) }
					<span className="redfox-thank-you-order-info__value">
						{ getMockData( infoType ) }
					</span>
				</div>
			</div>
		</>
	);
};

export { OrderInfoEdit as Edit };
